import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BlogPost } from '@/mocks/blogPosts';

interface BlogArticleProps {
  post: BlogPost;
}

const BlogArticle = ({ post }: BlogArticleProps) => {
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-2xl font-semibold text-black mt-8 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-black mt-4 mb-1">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-gray-600 text-base leading-relaxed ml-4 list-disc">{line.replace('- ', '')}</li>;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-gray-600 text-base leading-relaxed mb-3">{line}</p>;
    });
  };

  return (
    <article className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-8">
        <Link to="/" className="hover:text-black transition-colors cursor-pointer">Home</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/blog" className="hover:text-black transition-colors cursor-pointer">Blog</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-gray-600 truncate">{isEs ? post.categoryEs : post.category}</span>
      </div>

      {/* Category + Meta */}
      <div className="flex items-center gap-3 mb-4">
        <span className="bg-black text-white text-xs font-semibold px-3 py-1 rounded-full">
          {isEs ? post.categoryEs : post.category}
        </span>
        <span className="text-gray-400 text-xs">{post.date}</span>
        <span className="text-gray-300 text-xs">·</span>
        <span className="text-gray-400 text-xs">{post.readTime}</span>
      </div>

      {/* Title */}
      <h1 className="text-3xl md:text-4xl font-light text-black leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {isEs ? post.titleEs : post.title}
      </h1>

      {/* Excerpt */}
      <p className="text-gray-500 text-lg leading-relaxed mb-8 border-l-4 border-black pl-5">
        {isEs ? post.excerptEs : post.excerpt}
      </p>

      {/* Hero Image */}
      <div className="rounded-2xl overflow-hidden h-72 md:h-96 mb-10">
        <img src={post.img} alt={isEs ? post.titleEs : post.title} className="w-full h-full object-cover object-top" />
      </div>

      {/* Content */}
      <div className="prose-custom">
        {renderContent(isEs ? post.contentEs : post.content)}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        {post.tags.map(tag => (
          <span key={tag} className="bg-gray-50 text-gray-600 text-xs px-3 py-1.5 rounded-full border border-gray-100">
            {tag}
          </span>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-10 bg-black rounded-2xl p-8 text-center">
        <h3 className="text-white text-2xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {isEs ? '¿Listo para Comenzar?' : 'Ready to Get Started?'}
        </h3>
        <p className="text-gray-400 text-sm mb-6">
          {isEs ? 'Llame a Quinolux Glass & Mirrors para un estimado gratuito.' : 'Call Quinolux Glass & Mirrors for a free estimate in Las Vegas.'}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="tel:+17026042093" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-phone-fill"></i>
            (702) 604-2093
          </a>
          <Link to="/services" className="inline-flex items-center gap-2 border border-gray-600 text-white text-sm font-medium px-6 py-3 rounded-full hover:border-white transition-colors whitespace-nowrap cursor-pointer">
            {isEs ? 'Cotización Gratis' : 'Get Free Quote'}
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogArticle;
